import { useEffect, useMemo, useRef } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { PageLoading } from '@/components/common/PageStates'
import { AppButton } from '@/components/rfid/AppButton'
import { DocumentWizardShell } from '@/components/rfid/DocumentWizardShell'
import { SuccessHero } from '@/components/rfid/SuccessHero'
import { getMovement, type MovementId } from '@/config/movements'
import { useStoreHydration } from '@/hooks/useStoreHydration'
import { useLocale } from '@/i18n/LocaleProvider'
import { useDocumentWizardStore } from '@/stores/documentWizardStore'
import {
  SUCCESS_COMPLETE_TITLES,
  computeDispatchSummary,
  isCustoutDraftSubmitResult,
  parseSapSubmitDetails,
} from '@/utils/successSummary'
import { saveRecentDocumentScan } from '@/utils/recentScans'

function listPathForMovement(movementId: MovementId): string {
  const m = getMovement(movementId)
  if (m.listBacked) return `/movements/${m.id}/documents`
  if (m.id === 'transfer_out' || m.id === 'transfer_in') {
    return `/transfer/customer?movement=${m.id}`
  }
  return '/'
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[14px] border border-border bg-card p-3">
      <div className="text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 truncate text-[15px] font-black text-foreground">{value}</div>
    </div>
  )
}

export default function WizardSuccessPage() {
  const { t } = useLocale()
  const navigate = useNavigate()
  const location = useLocation()
  const hydrated = useStoreHydration(useDocumentWizardStore.persist)
  const routeState = (location.state ?? {}) as {
    message?: string
    referenceNumber?: string
  }
  const dispatchTimeRef = useRef(new Date())

  const reset = useDocumentWizardStore((s) => s.reset)
  const movementId = useDocumentWizardStore((s) => s.movementId)
  const document = useDocumentWizardStore((s) => s.document)
  const variant = useDocumentWizardStore((s) => s.variant)
  const lines = useDocumentWizardStore((s) => s.lines)
  const kitGroups = useDocumentWizardStore((s) => s.kitGroups)
  const kitBom = useDocumentWizardStore((s) => s.kitBom)
  const tagSet = useDocumentWizardStore((s) => s.tagSet)
  const transport = useDocumentWizardStore((s) => s.transport)
  const submitMessage = useDocumentWizardStore((s) => s.submitMessage)
  const submitRaw = useDocumentWizardStore((s) => s.submitRaw)

  const sapMessage = routeState.message || submitMessage || ''
  const isDraft = useMemo(
    () => isCustoutDraftSubmitResult(submitRaw, sapMessage),
    [submitRaw, sapMessage]
  )
  const sap = useMemo(
    () =>
      parseSapSubmitDetails(
        sapMessage,
        movementId ?? undefined,
        String(document?.DocumentNo ?? '')
      ),
    [sapMessage, movementId, document?.DocumentNo]
  )

  useEffect(() => {
    if (!hydrated || !sapMessage) return
    saveRecentDocumentScan({
      status: 'posted',
      sapMessage: sap.message,
      sapGeneratedDocument: isDraft ? undefined : sap.generatedDocument ?? undefined,
      sapMaterialDocument: sap.materialDocument ?? undefined,
    })
  }, [hydrated, sap.message, sap.generatedDocument, sap.materialDocument, isDraft, sapMessage])

  const summary = useMemo(
    () =>
      document && movementId
        ? computeDispatchSummary(lines, kitGroups, kitBom, tagSet, variant)
        : { totalLines: 0, fullyDispatched: 0 },
    [document, movementId, lines, kitGroups, kitBom, tagSet, variant]
  )

  if (!hydrated) {
    return <PageLoading tip="Restoring scan session…" />
  }

  if (!document || !movementId) {
    return <Navigate to="/" replace />
  }

  const movement = getMovement(movementId)
  const s = t.DocumentWizardSuccess
  const completeTitle = isDraft
    ? s.draftTitle
    : SUCCESS_COMPLETE_TITLES[movementId] ?? s.titleFallback
  const generatedDocDisplay = isDraft
    ? s.draftDocumentLabel
    : sap.generatedDocument ?? sap.trackingReference ?? routeState.referenceNumber ?? '—'
  const vehicle = String(transport.VEHICLE_NO || document.VehicleNo || '').trim()
  const dispatchTimeLabel = dispatchTimeRef.current.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  const customerName = String(document.CustName ?? 'Customer')

  const goHome = () => {
    reset()
    navigate('/', { replace: true })
  }

  const anotherDocument = () => {
    const path = listPathForMovement(movementId)
    reset()
    navigate(path, { replace: true })
  }

  return (
    <DocumentWizardShell
      step={4}
      title={`${movement.legacyName} — Submit`}
      subtitle={isDraft ? s.draftStepSubtitle : s.postedStepSubtitle}
      onBack={goHome}
      document={document}
      hideDocumentSummary
      footer={
        <div className="flex flex-col gap-2.5">
          <AppButton variant="outline" block onClick={anotherDocument}>
            {s.anotherDocument}
          </AppButton>
          <AppButton block onClick={goHome}>
            {s.home}
          </AppButton>
        </div>
      }
    >
      <SuccessHero
        title={completeTitle}
        subtitle={`${customerName} · Ref ${String(document.DocumentNo ?? '—')}`}
      />

      <div className="mb-3 rounded-[14px] border border-border bg-card p-3.5">
        <div className="text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">
          {isDraft ? s.sapDocumentLabel : s.generatedDocumentLabel}
        </div>
        <div className="mt-1 text-[18px] font-black text-foreground">{generatedDocDisplay}</div>
        {!isDraft && sap.materialDocument ? (
          <div className="mt-2 text-[12px] font-semibold text-muted-foreground">
            Material doc · {sap.materialDocument}
          </div>
        ) : null}
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2.5">
        <StatCard
          label={s.linesDispatched}
          value={`${summary.fullyDispatched}/${summary.totalLines}`}
        />
        <StatCard label={s.tagsScanned} value={String(tagSet.size)} />
        <StatCard label={s.vehicle} value={vehicle || '—'} />
        <StatCard label={s.dispatchTime} value={dispatchTimeLabel} />
      </div>

      {sap.message ? (
        <div className="rounded-[14px] border border-border bg-card p-3.5">
          <div className="text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">
            {isDraft ? s.statusLabel : s.sapResponseLabel}
          </div>
          <p className="mt-1 text-[13px] font-semibold leading-5 text-foreground">{sap.message}</p>
        </div>
      ) : null}
    </DocumentWizardShell>
  )
}
